"use client";

import React, { useEffect, useId, useRef, useState, useCallback } from "react";
import { DateRangeInput, GuestsSelector, Button } from "@fm/ui";
import { Search, MapPin, MapPinned, Users, ChevronDown } from "lucide-react";
import { loadGoogleMapsAPI } from "../utils/googleMapsLoader";
import { getReadableTextColor } from "../utils/readableColor";
import { add } from "date-fns";

export interface LocationData {
  lat: number;
  lng: number;
  city: string;
  state: string;
  formatted_address: string;
}

/**
 * A region as the search bar needs it: something to show, and its resolved
 * pickup so choosing it can fill the location field the shopper actually
 * sees. `pickup_source` is not included — the bar renders the resolved
 * values, it never needs to explain where they came from.
 *
 * Every `pickup_*` field is independently nullable: a region with no address
 * of its own and no fleet default still has to be selectable, just with an
 * empty/no point.
 */
export interface SearchBarRegion {
  id: string;
  name: string;
  pickup_location?: string | null;
  pickup_city?: string | null;
  pickup_state?: string | null;
  pickup_lat?: number | null;
  pickup_lng?: number | null;
}

/**
 * The fleet's own default pickup — what choosing "All regions" restores the
 * location field to. Same shape as the value the host already resolves for
 * its initial prefill, passed straight through rather than reshaped, so
 * there is exactly one place that knows how to turn a fleet's settings into
 * a location.
 */
export interface SearchBarDefaultPickup {
  lat: number | null;
  lng: number | null;
  city: string;
  state: string;
  locationText: string;
}

export interface EmbedSearchBarFormData {
  location: LocationData | null;
  locationText: string;
  from: string;
  to: string;
  adults: number;
  children: number;
  infants: number;
  pets: number;
  /** The chosen region, or `""` for every region. */
  regionId: string;
}

export interface EmbedSearchBarProps {
  googleMapsApiKey: string;
  /**
   * The regions to offer. Omitted or empty renders no picker at all, which is
   * every fleet today: the bar stays exactly as it is.
   *
   * A prop rather than a fetch: this bar is also the embedded widget, which
   * loads no webpage settings and so has no way of its own to learn a fleet's
   * regions. The host that knows them supplies them.
   */
  regions?: SearchBarRegion[];
  /**
   * What choosing "All regions" restores the location field to. Omitted or
   * null just clears the field — a fleet with no default pickup configured
   * at all.
   */
  defaultPickup?: SearchBarDefaultPickup | null;
  onSearch?: (data: EmbedSearchBarFormData) => void;
  initialData?: Partial<EmbedSearchBarFormData>;
  primaryColor?: string;
  /**
   * The Search button's own background (hex), when the host sets one apart
   * from `primaryColor` (the booking site's button color). Omitted, the
   * button uses `primaryColor`, as the embedded widget always has.
   */
  buttonColor?: string;
  /**
   * The Search button's label and icon color. Omitted, it is white or dark,
   * whichever reads better on the button.
   */
  buttonTextColor?: string;
  dateTextColor?: string;
  disabled?: boolean;
  className?: string;
}

interface Prediction {
  place_id: string;
  description: string;
}

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

const customStyles = `
    .embed-date-range > div {
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      min-width: 0 !important;
      height: auto !important;
      padding: 0rem 1rem !important;
    }
    .embed-date-range > div:focus,
    .embed-date-range > div:focus-within {
      ring: 0 !important;
      outline: none !important;
      box-shadow: none !important;
    }
    .embed-date-range svg {
      color: rgb(17 24 39) !important;
      width: 1.25rem !important;
      height: 1.25rem !important;
      margin-right: 5px !important;
    }
    .embed-date-range span {
      white-space: nowrap !important;
    }

    /* Force responsive visibility since Tailwind classes might be missing in package build */
    .embed-search-bar-stacked {
      display: flex !important;
    }
    .embed-search-bar-row {
      display: none !important;
    }

    @media (min-width: 1024px) {
      .embed-search-bar-stacked {
        display: none !important;
      }
      .embed-search-bar-row {
        display: flex !important;
      }
    }
  `;

export function EmbedSearchBar({
  googleMapsApiKey,
  regions,
  defaultPickup,
  onSearch,
  initialData,
  primaryColor,
  buttonColor,
  buttonTextColor,
  dateTextColor,
  disabled = false,
  className,
}: EmbedSearchBarProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null,
  );
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const [formData, setFormData] = useState<EmbedSearchBarFormData>({
    location: initialData?.location || null,
    locationText: initialData?.locationText || "",
    from: initialData?.from || "",
    to: initialData?.to || "",
    adults: initialData?.adults ?? 2,
    children: initialData?.children ?? 0,
    infants: initialData?.infants ?? 0,
    pets: initialData?.pets ?? 0,
    regionId: initialData?.regionId ?? "",
  });

  useEffect(() => {
    if (!googleMapsApiKey) return;
    loadGoogleMapsAPI(googleMapsApiKey)
      .then(() => setIsGoogleLoaded(true))
      .catch((error) => console.error("Error loading Google Maps API:", error));
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (!isGoogleLoaded) return;
    autocompleteServiceRef.current =
      new google.maps.places.AutocompleteService();
    const dummyDiv = document.createElement("div");
    placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
  }, [isGoogleLoaded]);

  useEffect(() => {
    // The location field is rendered twice (mobile stacked + desktop row) and
    // both copies share `inputRef`/`dropdownRef`, which only ever point to the
    // copy React commits last (desktop). Checking containment against a class
    // instead keeps this instance-agnostic, so a tap on the mobile dropdown
    // isn't treated as "outside" and dismissed before the selection click lands.
    const handleClickOutside = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest(".embed-location-field")) return;
      setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || !sessionTokenRef.current) return;
    if (input.length < MIN_CHARS) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: "us" },
        sessionToken: sessionTokenRef.current,
      },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(
            results.map((r) => ({
              place_id: r.place_id,
              description: r.description,
            })),
          );
          setShowDropdown(true);
          setActiveIndex(-1);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      },
    );
  }, []);

  const selectPlace = useCallback((placeId: string) => {
    if (!placesServiceRef.current || !sessionTokenRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId,
        fields: ["address_components", "formatted_address", "geometry"],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !place?.geometry?.location
        )
          return;

        const components = place.address_components || [];
        const componentMap = new Map<
          string,
          google.maps.GeocoderAddressComponent
        >();
        components.forEach((c) => {
          c.types.forEach((t) => {
            if (!componentMap.has(t)) componentMap.set(t, c);
          });
        });

        const city =
          componentMap.get("locality")?.long_name ??
          componentMap.get("sublocality")?.long_name ??
          componentMap.get("administrative_area_level_3")?.long_name ??
          "";
        const state =
          componentMap.get("administrative_area_level_1")?.short_name ?? "";

        const locationData: LocationData = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          city,
          state,
          formatted_address: place.formatted_address || `${city}, ${state}`,
        };

        setFormData((prev) => ({
          ...prev,
          location: locationData,
          locationText: locationData.formatted_address,
        }));

        setPredictions([]);
        setShowDropdown(false);

        // Reset session token after place selection (ends billing session)
        sessionTokenRef.current =
          new google.maps.places.AutocompleteSessionToken();
      },
    );
  }, []);

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      locationText: value,
      location: null,
      // Typing is the shopper overriding the named region with their own
      // address, so it clears the picker back to "All regions". This lives
      // in the real typing handler rather than an effect watching
      // `locationText`: an effect would also fire on `handleRegionChange`'s
      // own programmatic write below and immediately clear the region it
      // just set.
      regionId: "",
    }));

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < MIN_CHARS) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchPredictions(value);
    }, DEBOUNCE_MS);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
    } else if (
      e.key === "Enter" &&
      activeIndex >= 0 &&
      predictions[activeIndex]
    ) {
      e.preventDefault();
      selectPlace(predictions[activeIndex].place_id);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleDateRangeChange = useCallback(
    (range: [string, string] | null) => {
      setFormData((prev) => ({
        ...prev,
        from: range?.[0] || "",
        to: range?.[1] || "",
      }));
    },
    [],
  );

  const handleGuestsChange = useCallback(
    (guests: {
      adults: number;
      children: number;
      infants: number;
      pets: number;
    }) => {
      setFormData((prev) => ({ ...prev, ...guests }));
    },
    [],
  );

  const fieldIdPrefix = useId();

  const handleRegionChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const regionId = event.target.value;

      if (!regionId) {
        // "All regions" restores the fleet's own default pickup — the
        // question that was being asked before a region narrowed it.
        const hasPoint =
          defaultPickup?.lat != null && defaultPickup?.lng != null;
        setFormData((previous) => ({
          ...previous,
          regionId: "",
          location:
            defaultPickup && hasPoint
              ? {
                  lat: defaultPickup.lat!,
                  lng: defaultPickup.lng!,
                  city: defaultPickup.city,
                  state: defaultPickup.state,
                  formatted_address: defaultPickup.locationText,
                }
              : null,
          locationText: defaultPickup?.locationText ?? "",
        }));
        return;
      }

      const region = regions?.find((candidate) => candidate.id === regionId);
      const lat = region?.pickup_lat ?? null;
      const lng = region?.pickup_lng ?? null;
      const hasPoint = lat !== null && lng !== null;

      // A programmatic fill, not the shopper typing: `handleLocationChange`
      // is the only place that clears the region, so this write is safe.
      setFormData((previous) => ({
        ...previous,
        regionId,
        location: hasPoint
          ? {
              lat: lat!,
              lng: lng!,
              city: region?.pickup_city ?? "",
              state: region?.pickup_state ?? "",
              formatted_address: region?.pickup_location ?? "",
            }
          : null,
        // Honest and empty when the region has no resolved pickup at all —
        // it stays selectable, it just has nothing to show.
        locationText: region?.pickup_location ?? "",
      }));
    },
    [regions, defaultPickup],
  );

  // Both layouts are always rendered and CSS hides one at 1024px (see
  // `customStyles`), so open the picker of the one that is showing.
  const openDatePicker = () => {
    const layout = window.matchMedia?.("(min-width: 1024px)").matches
      ? ".embed-search-bar-row"
      : ".embed-search-bar-stacked";
    formRef.current
      ?.querySelector<HTMLElement>(
        `${layout} .embed-date-range [aria-haspopup="dialog"]`,
      )
      ?.click();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // A search needs both dates: without them nothing can be priced, and the
    // results could only say nothing was found. Ask for them instead.
    if (!formData.from || !formData.to) {
      openDatePicker();
      return;
    }
    onSearch?.(formData);
  };

  const darkenColor = (hex: string, percent: number = 10): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent));
    const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(2.55 * percent));
    const b = Math.max(0, (num & 0x0000ff) - Math.round(2.55 * percent));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  };

  const effectiveColor = primaryColor || "#ed8f0c";

  const hexToRgba = (hex: string, alpha: number): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = (num >> 16) & 0xff;
    const g = (num >> 8) & 0xff;
    const b = num & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const borderColor = hexToRgba(effectiveColor, 0.3);

  const buttonBackground = buttonColor || effectiveColor;

  const buttonStyle = {
    backgroundColor: buttonBackground,
  };

  // Label and icon contrast with the button's base color. Hover only darkens
  // it a little, so the same pick stays readable there.
  const buttonLabelColor =
    buttonTextColor || getReadableTextColor(buttonBackground);

  const dateRange: [string, string] | null = formData.from
    ? [formData.from, formData.to || ""]
    : null;

  const locationField = (
    <div className="embed-location-field flex-1 min-w-0 relative">
      <div
        className="flex items-center pr-4 py-3"
        style={{ paddingLeft: "10px" }}
      >
        <MapPin
          className="w-5 h-5 text-gray-900 flex-shrink-0"
          style={{ marginRight: "5px" }}
        />
        <input
          ref={inputRef}
          type="text"
          value={formData.locationText}
          onChange={handleLocationChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0) setShowDropdown(true);
          }}
          placeholder="Pickup location"
          disabled={disabled}
          autoComplete="off"
          className="w-full bg-transparent border-0 p-0 text-sm font-medium text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none truncate"
        />
      </div>

      {/* Predictions Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-[10000] overflow-hidden"
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                index === activeIndex
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectPlace(prediction.place_id)}
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {prediction.description}
            </button>
          ))}
          <div className="px-4 py-1.5 border-t border-gray-100 flex justify-end">
            <svg
              width="120"
              height="14"
              viewBox="0 0 120 14"
              xmlns="http://www.w3.org/2000/svg"
            >
              <text
                x="0"
                y="11"
                fill="#9aa0a6"
                fontSize="10"
                fontFamily="Arial, sans-serif"
              >
                powered by
              </text>
              <text x="58" y="11" fontSize="10" fontFamily="Arial, sans-serif">
                <tspan fill="#4285F4">G</tspan>
                <tspan fill="#EA4335">o</tspan>
                <tspan fill="#FBBC05">o</tspan>
                <tspan fill="#4285F4">g</tspan>
                <tspan fill="#34A853">l</tspan>
                <tspan fill="#EA4335">e</tspan>
              </text>
            </svg>
          </div>
        </div>
      )}
    </div>
  );

  /**
   * The bar renders every field twice — a stacked copy for phones and a pill
   * for desktop, one of them hidden by CSS — so the picker takes the layout's
   * name to keep its `id` unique and its label pointing at its own `<select>`.
   */
  const regionFieldFor = (layout: string) =>
    regions?.length ? (
      <div className="flex-1 min-w-0 relative">
        <div className="flex items-center px-4 py-3">
          <MapPinned
            className="w-5 h-5 text-gray-900 flex-shrink-0"
            style={{ marginRight: "5px" }}
          />
          <label
            htmlFor={`${fieldIdPrefix}-region-${layout}`}
            className="sr-only"
          >
            Region
          </label>
          <select
            id={`${fieldIdPrefix}-region-${layout}`}
            value={formData.regionId}
            onChange={handleRegionChange}
            disabled={disabled}
            className="w-full bg-transparent border-0 p-0 text-sm font-medium text-gray-900 focus:ring-0 focus:outline-none cursor-pointer"
          >
            <option value="">All regions</option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    ) : null;

  const dateField = (
    <div className="flex-1 relative py-1.5 md:py-0">
      <DateRangeInput
        value={dateRange}
        onChange={handleDateRangeChange}
        placeholder="Select dates"
        theme="light"
        primaryColor={primaryColor || "#083C91"}
        textColor={dateTextColor}
        showLabels={false}
        showToday={true}
        minDate={add(new Date(), { days: -1 })}
        disabled={disabled}
        className="embed-date-range py-0"
      />
    </div>
  );

  const guestsField = (
    <div className="flex-1 md:flex-[0.8] min-w-0 relative">
      <div className="flex items-center px-4 py-3 md:py-3.5 md:px-5">
        <div className="flex items-center flex-1 min-w-0 cursor-pointer">
          <Users
            className="w-5 h-5 text-gray-900 flex-shrink-0"
            style={{ marginRight: "5px" }}
          />
          <div className="flex-1">
            <GuestsSelector
              adults={formData.adults}
              childrenCount={formData.children}
              infants={formData.infants}
              pets={formData.pets}
              onGuestsChange={handleGuestsChange}
              disabled={disabled}
              textColor="black"
              className="w-full bg-transparent border-0 p-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none text-sm font-medium h-auto shadow-none cursor-pointer"
            />
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400 ml-2" />
        </div>
      </div>
    </div>
  );

  // `data-site-area` marks the button for the booking site's live preview of
  // its site design (FLEE-172); it is inert everywhere else.
  const searchButton = (
    <Button
      type="submit"
      disabled={disabled}
      data-site-area="button"
      className="rounded-full inline-flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
      style={{
        ...buttonStyle,
        height: "40px",
        padding: "8px 24px",
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "20px",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = darkenColor(
          buttonBackground,
          10,
        );
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = buttonBackground;
      }}
    >
      <Search
        className="h-5 w-5"
        style={{ marginRight: "5px", color: buttonLabelColor }}
      />
      <span
        style={{
          color: buttonLabelColor,
          fontSize: "14px",
          fontWeight: 500,
          lineHeight: "20px",
        }}
      >
        Search
      </span>
    </Button>
  );

  return (
    <div className={className}>
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      <form ref={formRef} onSubmit={handleSubmit}>
        {/* Mobile: stacked layout - default flex, hidden on lg via CSS.
            No `overflow-hidden` here: it would clip the location predictions
            dropdown (absolute, escaping the card). The card stays rounded
            because `bg-white rounded-2xl` clips the background, and the row
            dividers sit on the straight edges, not the rounded corners. */}
        <div
          className="embed-search-bar-stacked flex-col gap-0 bg-white rounded-2xl border"
          style={{
            borderColor,
            boxShadow: "0 10px 10px -10px rgba(0,0,0,0.08)",
          }}
        >
          {regionFieldFor("stacked") && (
            <div className="border-b border-gray-100">
              {regionFieldFor("stacked")}
            </div>
          )}
          <div className="border-b border-gray-100">{locationField}</div>
          <div className="border-b border-gray-100">{dateField}</div>
          <div className="border-b border-gray-100">{guestsField}</div>
          <div className="p-3">
            <div className="w-full">
              <Button
                type="submit"
                disabled={disabled}
                data-site-area="button"
                className="w-full rounded-full inline-flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                style={{
                  ...buttonStyle,
                  height: "44px",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkenColor(
                    buttonBackground,
                    10,
                  );
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = buttonBackground;
                }}
              >
                <Search
                  className="h-5 w-5"
                  style={{ marginRight: "5px", color: buttonLabelColor }}
                />
                <span
                  style={{
                    color: buttonLabelColor,
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  Search
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop: horizontal pill layout - default hidden, flex on lg via CSS */}
        <div
          className="embed-search-bar-row items-center w-full max-w-4xl bg-white rounded-full border transition-shadow duration-300"
          style={{
            borderColor,
            boxShadow: "0 10px 10px -10px rgba(0,0,0,0.08)",
            padding: "0.1rem 0rem",
          }}
        >
          {regionFieldFor("row") && (
            <div className="flex-1 min-w-0 relative">
              {regionFieldFor("row")}
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 bg-gray-100" />
            </div>
          )}

          <div className="flex-1 min-w-0 relative">
            {locationField}
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 bg-gray-100" />
          </div>

          <div className="flex-1 relative">
            {dateField}
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 bg-gray-100" />
          </div>

          {guestsField}

          <div className="pr-1 py-0 pl-2">{searchButton}</div>
        </div>
      </form>
    </div>
  );
}
